import { useState, useEffect } from 'react';
import { LogEntryWithType, CallLog, TextLog, VoiceMailLog, FormGroup } from '@/types/TInbox';
import moment from 'moment';
import { processIntakeForms } from '@/pages/ReceptionistDashboard/LogsComponent/Inbox/components/IntakeFormGroup';
import { safeJSONParse } from '@/utils/helper';

export const useLogProcessor = (
	callLogsData: any,
	textLogsData: any,
	voicemailsData: any,
	dateRange?: [any, any] | null,
) => {
	const [allLogs, setAllLogs] = useState<LogEntryWithType[]>([]);
	const [formGroups, setFormGroups] = useState<FormGroup[]>([]);

	const convertToLogEntry = (
		log: CallLog | TextLog | VoiceMailLog | any,
		type: 'call' | 'text' | 'voicemail',
	): LogEntryWithType => {
		const getContact = () => {
			if (type === 'call') {
				const callLog = log as CallLog;
				return {
					name: callLog.name || undefined,
					phone: callLog.from || '',
				};
			} else if (type === 'text') {
				const textLog = log as TextLog;
				return {
					name: textLog.name || undefined,
					phone: textLog.from || '',
				};
			} else if (type === 'voicemail') {
				const vmLog = log as VoiceMailLog;
				return {
					name: vmLog.name || undefined,
					phone: vmLog.from || '',
				};
			}
			return { phone: '' };
		};

		const getMessage = () => {
			if (type === 'call') {
				const callLog = log as CallLog;
				return callLog.callSummary || 'Call completed';
			} else if (type === 'text') {
				const textLog = log as TextLog;
				return textLog.text || 'Text message received';
			} else if (type === 'voicemail') {
				const vmLog = log as VoiceMailLog;
				return vmLog.transcript || vmLog.voicemail_transcript || 'Voicemail received';
			}
			return '';
		};

		const getStatus = (): 'answered' | 'missed' | 'unread' | 'read' | 'pending' => {
			if (type === 'call') {
				const callLog = log as CallLog;
				return callLog.isRead === false ? 'unread' : 'answered';
			}
			if (type === 'voicemail') {
				return 'unread';
			}
			if (type === 'text') {
				return 'read';
			}
			return 'pending';
		};

		let logDate;
		try {
			logDate = moment(log.date);
			if (!logDate.isValid() && log.startedTimestamp) {
				logDate = moment(log.startedTimestamp);
			}
			if (!logDate.isValid()) {
				logDate = moment();
			}
		} catch {
			logDate = moment();
		}

		const now = moment();
		const timeAgo = logDate.fromNow();
		const isToday = logDate.isSame(now, 'day');

		return {
			id: log.id || log.sid || Math.random().toString(),
			date: log.date || logDate.format(),
			timeAgo,
			isToday,
			type,
			contact: getContact(),
			message: getMessage(),
			status: getStatus(),
			callSid: (log as CallLog)?.callSid,
			duration: (log as CallLog)?.duration,
			from: log?.from,
			transcript: (log as CallLog | VoiceMailLog)?.transcript,
			callSummary: (log as CallLog)?.callSummary,
			callScore: (log as CallLog)?.callScore,
			callSentiments: (log as CallLog)?.callSentiments,
			intakeResponses: (log as CallLog)?.intakeResponses,
			intake_questions: (log as CallLog)?.intake_questions,
			intake_questions_workflows: (log as CallLog)?.intake_questions_workflows,
			text: (log as TextLog)?.text,
			voicemail_transcript: (log as VoiceMailLog)?.voicemail_transcript,
			originalLog: log,
		};
	};

	// Helper function to check if a log falls within the date range
	const isWithinDateRange = (logDate: string | moment.Moment) => {
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			return true; // No date filter, include all
		}

		try {
			const logMoment = moment(logDate);
			if (!logMoment.isValid()) {
				console.warn('Invalid log date:', logDate);
				return true; // Include invalid dates for now
			}

			const startDate = moment(dateRange[0]).startOf('day');
			const endDate = moment(dateRange[1]).endOf('day');

			const isInRange = logMoment.isBetween(startDate, endDate, null, '[]');

			return isInRange;
		} catch (error) {
			console.error('Error in date filtering:', error, logDate);
			return true; // Include on error
		}
	};

	useEffect(() => {
		const processedLogs: LogEntryWithType[] = [];
		const formLogsForProcessing: LogEntryWithType[] = []; // Separate array for form processing

		// Process call logs
		if (callLogsData) {
			const callLogs = Array.isArray(callLogsData) ? callLogsData : callLogsData.callLogs || [];

			callLogs.forEach((log: CallLog) => {
				// Check if has intakeResponses for form processing
				let hasValidIntakeData = false;
				if (log?.intakeResponses && log.intakeResponses?.length > 0) {
					try {
						const parsed = safeJSONParse(log.intakeResponses, {});
						if (parsed && Object.keys(parsed).length > 0) {
							hasValidIntakeData = true;
						}
					} catch (err) {
						console.log('Error parsing intake responses:', err);
					}
				}

				// Add form log to separate array for processing (not sidebar)
				if (hasValidIntakeData) {
					const formLog: LogEntryWithType = {
						id: log.id || log.callSid || Math.random().toString(),
						date: log.date,
						timeAgo: 'now',
						isToday: true,
						type: 'form',
						contact: {
							name: log.name || undefined,
							phone: log.from || '',
						},
						message: 'Intake form completed',
						status: 'pending',
						callSid: log.callSid,
						duration: log.duration,
						from: log.from,
						transcript: log.transcript,
						callSummary: log.callSummary,
						callScore: log.callScore,
						callSentiments: log.callSentiments,
						intakeResponses: log.intakeResponses,
						intake_questions: log.intake_questions,
						intake_questions_workflows: log.intake_questions_workflows,
						originalLog: log,
					};
					formLogsForProcessing.push(formLog);
				}

				// Add regular call log to main logs (for sidebar)
				processedLogs.push(convertToLogEntry(log, 'call'));
			});
		}

		// Process text logs with date filtering
		if (textLogsData) {
			let textLogs: any[] = [];
			if (Array.isArray(textLogsData)) {
				textLogs = textLogsData;
			} else if (textLogsData.textLogs && Array.isArray(textLogsData.textLogs)) {
				textLogs = textLogsData.textLogs;
			}

			console.log('Processing text logs:', textLogs.length, 'with date filter:', !!dateRange);

			textLogs.forEach((log: any) => {
				// Apply date filter for text logs
				const logDate = log.date || log.startedTimestamp;
				const shouldInclude = isWithinDateRange(logDate);

				console.log('Text log date check:', {
					logId: log.id || log.sid,
					logDate,
					shouldInclude,
				});

				if (shouldInclude) {
					const convertedTextLog: TextLog = {
						id: log.sid || log.id || Math.random().toString(),
						date: log.date,
						timeAgo: '',
						isToday: false,
						to: log.to,
						from: log.from || log.forwardedFrom || 'Unknown',
						text: log.text,
						textType: log.textType,
						deliveryStatus: log.deliveryStatus,
						errorResolve: log.errorResolve,
						errorText: log.errorText,
						workflow: log.workflow,
						fallbackResentText: log.fallbackResentText,
						startedTimestamp: log.startedTimestamp,
						name: log.name || log.forwardedFrom || undefined,
					};
					processedLogs.push(convertToLogEntry(convertedTextLog, 'text'));
				}
			});
		}

		// Process voicemail logs with date filtering
		if (voicemailsData) {
			const voicemailLogs = Array.isArray(voicemailsData)
				? voicemailsData
				: voicemailsData.voiceMailLogs || [];
			console.log(
				'Processing voicemail logs:',
				voicemailLogs.length,
				'with date filter:',
				!!dateRange,
			);

			voicemailLogs.forEach((log: VoiceMailLog) => {
				// Apply date filter for voicemails
				const shouldInclude = isWithinDateRange(log.date);

				console.log('Voicemail log date check:', {
					logId: log.id,
					logDate: log.date,
					shouldInclude,
				});

				if (shouldInclude) {
					processedLogs.push(convertToLogEntry(log, 'voicemail'));
				}
			});
		}

		const sortedLogs = processedLogs.sort((a, b) => {
			return moment(b.date).unix() - moment(a.date).unix();
		});

		setAllLogs(sortedLogs);

		// Process form groups using the separate form logs array
		const allLogsWithForms = [...sortedLogs, ...formLogsForProcessing];
		const processedFormGroups = processIntakeForms(allLogsWithForms);
		setFormGroups(processedFormGroups);
	}, [callLogsData, textLogsData, voicemailsData, dateRange]);

	return { allLogs, formGroups };
};
